import type { PlanShare } from './planShare';
/** As the API of the invoices module sends it (F34). Amounts are absent without the field rights. */
export type InvoiceKsefState = 'brak' | 'oczekuje' | 'wysylana' | 'przyjeta' | 'odrzucona' | 'offline';
export type Buyer = { name: string; nip: string | null; addressL1: string; addressL2: string; email: string };
export type InvoiceLine = { id: string; sort: number; name: string; unit: string; quantityMilli: number; unitNetGrosze?: number; netGrosze?: number; version: number };
export type Invoice = {
  id: string;
  kind: 'VAT' | 'ZAL' | 'ROZ' | 'KOR';
  state: 'robocza' | 'wystawiona' | 'zewnetrzna' | 'anulowana';
  /** Null for `zewnetrzna`: issued outside the program, downloaded from KSeF (F41). */
  eventId: string | null;
  eventName: string;
  quoteVersionId: string | null;
  quoteVersionNumber: number | null;
  contractorId: string | null;
  buyer: Buyer;
  number: string | null;
  issueDate: string | null;
  saleDate: string;
  dueDate: string;
  paymentMethod: 'przelew' | 'gotowka' | 'barter';
  /** Lines: by the rules of invoice lines (new drafts), one per quote section, or one per quote line ("Szczegółowo"). */
  grouping: Grouping;
  /** `reguly`: the rule the lines were made by; null when none matched (lines by sections) or another mode. */
  lineRuleName: string | null;
  /** The card only: the PDF route gives this user the PDF (every amount and the company's account, F35). */
  pdfAllowed?: boolean;
  vatBp: number;
  netGrosze?: number;
  vatGrosze?: number;
  grossGrosze?: number;
  differenceGrosze?: number;
  /** „Zostaw fakturę” was said for this very difference (owner 2026-11-01); absent with the difference (rule 7a). */
  differenceDismissed?: boolean;
  /** Card only: the server's KSeF is the test one (numbers `TEST/…`, no legal force). */
  ksefTest?: boolean;
  /** Card of a draft (and of a rejected invoice: „Nadaj nowy numer”) only: the series its number comes from. */
  issueSeries?: string;
  /** Card only: a mail server is set up (else e-mail only reaches the server's log). */
  mailConfigured?: boolean;
  /** The planned e-mail could not go (its PDF could not be made) — send it by hand. */
  autoSendFailed?: boolean;
  /** „Wyślij automatycznie, gdy KSeF nada numer”: the planned e-mail to the client (null: none). */
  autoSend?: { to: string; cc: string; at: string } | null;
  /** KSeF rejected the invoice and so cancelled its planned e-mail. */
  autoSendCancelled?: boolean;
  /** A draft of an event with a mixed payment plan: it covers only the invoiced share (absent without the amounts). */
  planShare?: PlanShare | null;
  notes: string;
  ksefState: InvoiceKsefState;
  ksefNumber: string | null;
  ksefError: string | null;
  /** `440`: the number is used in KSeF for another document → "Nadaj nowy numer i wyślij". */
  ksefErrorCode: string | null;
  previousNumbers: string[];
  /** „Pobierz UPO”: KSeF's receipt of the invoice is stored; absent when the role does not see the KSeF number. */
  upoAvailable?: boolean;
  /** A draft of a quote version that is no longer accepted. */
  outdated: boolean;
  ksefDeadline: string | null;
  test: boolean;
  /** F35: the stored PDF (after the KSeF number; at once offline); null while waiting, and when rejected or cancelled. */
  pdfFileId: string | null;
  /** F35 "Wyślij do klienta": when and to whom; where the e-mail is now. */
  sentAt: string | null;
  sentTo: string | null;
  /** `no_smtp`: it only reached the server log (no mail server) — the client did not get it. */
  emailStatus: 'queued' | 'sent' | 'failed' | 'no_smtp' | null;
  /** A "Wyślij do klienta" is possible now (valid PDF; no e-mail with it waiting or delivered). */
  canSend: boolean;
  /** A mail server delivered a PDF of this invoice to the client. */
  clientHasPdf: boolean;
  /** The client got an earlier PDF (before a correction): the current one should be sent. */
  clientPdfOutdated: boolean;
  /** F41 warnings: the buyer of an external invoice is not in contractors; another KSeF invoice has our number. */
  attention: Array<{ code: 'brak_kontrahenta' | 'numer_w_ksef'; message: string }>;
  /** KOR (F40): why the invoice is corrected, and the corrected invoice. */
  correctionReason: string;
  corrected: { id: string; kind: 'VAT' | 'ZAL' | 'ROZ' | 'KOR'; number: string | null; issueDate: string | null; ksefNumber: string | null } | null;
  /** A correction of an advance (KOR_ZAL): the advance before this correction (amounts with the amount fields). */
  advanceBefore: Invoice['advances'];
  /** F40: "Korekta…" possible now (in KSeF, correctable kind, the server's test / real mode). */
  correctable: boolean;
  /** ZAL (F40): the payment plan's installment whose payment it documents. */
  installmentId: string | null;
  /** ROZ (F40): the advance invoices it settles (amounts only with the invoice amount fields). */
  advances: Array<{ id: string; number: string | null; issueDate: string | null; ksefNumber: string | null; netGrosze?: number; vatGrosze?: number; grossGrosze?: number }>;
  lines: InvoiceLine[];
  /** KOR (F40): the lines as the corrected invoice has them (ids of their lines in `lines`). */
  linesBefore: InvoiceLine[];
  version: number;
};
export type InvoiceListItem = Omit<Invoice, 'lines' | 'linesBefore' | 'advances' | 'advanceBefore'>;

export type InvoiceSettings = {
  sellerName: string;
  sellerNip: string;
  sellerAddressL1: string;
  sellerAddressL2: string;
  sellerIban?: string;
  sellerBankName: string;
  sellerEmail: string;
  sellerPhone: string;
  /** Owner 2026-10-31: the quote's footer (wzór F); not on invoices. */
  sellerWww: string;
  vatStatus: 'czynny' | 'zwolniony' | null;
  exemptionBasis: 'ustawa' | 'dyrektywa' | 'inna' | null;
  exemptionText: string;
  defaultPaymentDays: number;
  numberPattern: string;
  numberReset: 'miesiac' | 'rok';
  footerNote: string;
  /** F35: the template of "Wyślij do klienta". */
  emailSubject: string;
  emailBody: string;
  /** "Do wystawienia od": events that ended before this day are not in "Do wystawienia". */
  toIssueFrom: string;
  nextNumber: number;
  /** The series "Następny numer" moves: `TEST/FV` while KSeF works in the test or demo environment. */
  series: 'FV' | 'TEST/FV';
  missing: string[];
  version: number;
};

/** F35 "Wyślij do klienta": the dialog's start (the buyer's e-mail, the template filled in). */
export type SendPreview = { to: string; cc: string; subject: string; body: string; emailMode: 'smtp' | 'log' };

export const INVOICES_KEY = ['invoices'] as const;
export const INVOICES_RESOURCE = 'invoices.invoices';
export const LINES_RESOURCE = 'invoices.lines';
export const INVOICE_SETTINGS_RESOURCE = 'invoices.settings';

export const PAYMENT_LABELS: Record<Invoice['paymentMethod'], string> = { przelew: 'przelew', gotowka: 'gotówka', barter: 'barter' };

/** "Uzupełnij dane firmy" names the missing settings in Polish. */
export const MISSING_LABELS: Record<string, string> = {
  sellerName: 'nazwa firmy',
  sellerNip: 'NIP',
  sellerAddressL1: 'adres',
  sellerIban: 'numer rachunku',
  vatStatus: 'status VAT',
  exemptionBasis: 'podstawa zwolnienia (rodzaj)',
  exemptionText: 'podstawa zwolnienia (przepis)',
};

/** "07.11.2026" from `2026-11-07`. */
export const dayText = (day: string | null) => (day ? `${day.slice(8, 10)}.${day.slice(5, 7)}.${day.slice(0, 4)}` : '—');
/** "5260250995" → "526-025-09-95". */
export const nipText = (nip: string) => `${nip.slice(0, 3)}-${nip.slice(3, 6)}-${nip.slice(6, 8)}-${nip.slice(8)}`;
/** "PL61109010140000071219812874" → "61 1090 1014 0000 0712 1981 2874". */
export const ibanText = (iban: string) => {
  const d = iban.replace(/^PL/, '');
  return `${d.slice(0, 2)} ${d.slice(2).replace(/(.{4})/g, '$1 ').trim()}`;
};

export type Grouping = 'reguly' | 'sekcje' | 'pozycje';
/** The three modes of a draft's lines (owner 2026-10-20 23:51: "według reguł" is the default). */
/** "Podgląd PDF" / "Pobierz PDF (projekt)" of a draft: only where the server gives the PDF (review I1, F35 rule). */
export const draftPdfAllowed = (inv: Pick<Invoice, 'state' | 'pdfAllowed'>): boolean => inv.state === 'robocza' && inv.pdfAllowed === true;
export const GROUPING_LABELS: Record<Grouping, string> = { reguly: 'Według reguł', sekcje: 'Według sekcji', pozycje: 'Szczegółowo' };

/** "Finanse → Ustawienia → Reguły pozycji faktury" (owner 2026-10-20 23:50). */
export type Tri = 'tak' | 'nie' | 'dowolnie';
export type LineRule = {
  id: string;
  sort: number;
  name: string;
  equipment: Tri;
  services: Tri;
  serviceIds: string[];
  eventKind: 'impreza' | 'wypozyczenie' | 'dowolny';
  lineText: string;
  transport: 'osobno' | 'w_pozycji';
  transportText: string;
  version: number;
};
export type LineRulesView = { rules: LineRule[]; services: Array<{ id: string; name: string }> };
export type LineRuleFields = Omit<LineRule, 'id' | 'sort' | 'version'>;
/** The user sees "wydarzenie", never "impreza" (coordinator 2026-10-21); the stored key stays `impreza`. */
export const EVENT_KIND_LABELS: Record<LineRule['eventKind'], string> = { dowolny: 'dowolny', impreza: 'wydarzenie', wypozyczenie: 'wypożyczenie' };

/** "Sprzęt: tak · Usługi: Realizacja dźwięku · Rodzaj: wypożyczenie" — the conditions of a rule in one line. */
export function conditionsText(r: Pick<LineRule, 'equipment' | 'services' | 'serviceIds' | 'eventKind'>, serviceNames: ReadonlyMap<string, string>): string {
  const parts: string[] = [];
  if (r.equipment !== 'dowolnie') parts.push(r.equipment === 'tak' ? 'jest sprzęt' : 'bez sprzętu');
  if (r.services !== 'dowolnie') {
    const names = r.serviceIds.map((id) => serviceNames.get(id) ?? 'usunięta usługa');
    const what = names.length === 0 ? 'usługi' : names.join(' lub ');
    parts.push(r.services === 'tak' ? `jest ${what}` : `bez: ${what}`);
  }
  if (r.eventKind !== 'dowolny') parts.push(EVENT_KIND_LABELS[r.eventKind]);
  return parts.length === 0 ? 'zawsze' : parts.join(' · ');
}
/** "Do wystawienia" (owner 2026-10-20 23:42): a finished event with an accepted quote still to invoice. */
export type ToIssueRow = {
  eventId: string;
  eventName: string;
  startsAt: string;
  endsAt: string;
  eventStatus: string;
  contractorId: string | null;
  clientName: string;
  versionId: string;
  versionNumber: number;
  /** Absent without the right to the invoice amounts. */
  remainingGrossGrosze?: number;
  /** The event's draft invoice ("projekt"), or null. */
  draftId: string | null;
  /** The quote version of the draft; outdated: not the accepted one any more ("Przebuduj z v…"). */
  draftVersionNumber: number | null;
  draftOutdated: boolean;
  /** A mixed payment plan: the amount is its invoiced share (absent without the invoice amounts). */
  planShare?: PlanShare;
};
/** "Faktura zaliczkowa" / "rozliczeniowa" / "korygująca" (F40), plain "Faktura" for a VAT invoice. */
export const KIND_TITLES: Record<Invoice['kind'], string> = { VAT: 'Faktura', ZAL: 'Faktura zaliczkowa', ROZ: 'Faktura rozliczeniowa', KOR: 'Faktura korygująca' };
